/**
 * Unsaved changes, and not losing them by accident.
 *
 * A form whose edits exist only in the page lost them without a word when
 * somebody switched tab or desk, or reloaded — and the Intake page tested its
 * UNSAVED settings, so a desk looked configured right up until they were
 * gone. A screen with edits registers them here; switching tab or desk asks
 * first (`confirmDiscard`), and the browser warns before a reload or close.
 *
 * In-app links are not blocked (BrowserRouter has no blocker); a screen that
 * matters keeps its draft in the browser as well (`draftStore`) and offers it
 * back.
 */
import { useEffect, useId } from 'react';

const dirty = new Map<string, string>();

function onBeforeUnload(e: BeforeUnloadEvent): void {
  if (dirty.size === 0) return;
  e.preventDefault();
  // Required by some browsers to show the prompt at all.
  e.returnValue = '';
}

/** While `isDirty`, this screen has edits that leaving would lose. `label` names them in the prompt. */
export function useUnsavedChanges(isDirty: boolean, label: string): void {
  const id = useId();
  useEffect(() => {
    if (!isDirty) return;
    dirty.set(id, label);
    if (dirty.size === 1) window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      dirty.delete(id);
      if (dirty.size === 0) window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [id, isDirty, label]);
}

/** What has unsaved changes right now, by label. */
export function unsavedLabels(): string[] {
  return [...new Set(dirty.values())];
}

/** Ask before a switch that would lose unsaved changes. True means go ahead. */
export function confirmDiscard(ask: (message: string) => boolean = (m) => window.confirm(m)): boolean {
  const labels = unsavedLabels();
  if (labels.length === 0) return true;
  return ask(`You have unsaved changes to ${labels.join(', ')}. Leave without saving them?`);
}

/**
 * A draft kept in this browser, per key, so an accidental reload or a click
 * elsewhere does not throw an afternoon's settings away. Best effort: storage
 * can be full, disabled or private, and none of that may break the page.
 */
export const draftStore = {
  read<T>(key: string): { value: T; savedAt: string } | null {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as { value: T; savedAt: string }) : null;
    } catch {
      return null;
    }
  },
  write<T>(key: string, value: T): void {
    try {
      window.localStorage.setItem(key, JSON.stringify({ value, savedAt: new Date().toISOString() }));
    } catch {
      /* best effort */
    }
  },
  clear(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* best effort */
    }
  },
};
